import { useState } from "react";

import { useQueryClient } from "@tanstack/react-query";

import { useGenerateDatasetAdmArea } from "@/api/dataset/useDatasetAPI";
import NotificationContent from "@/components/notification/notificationContent";
import { MESSAGE } from "@/constants/message";
import useToast, { EToastType } from "@/hooks/ui/useToast";
import { GenerateAdmAreaResponse } from "@/types/dataset";

/** What the last attempt left on screen: nothing, the partial-match warning, or
 * the failure alert. A success leaves nothing behind, it moves the view on. */
export type AdmAreaMappingOutcome =
  | { kind: "partial"; counts: GenerateAdmAreaResponse["data"] }
  | { kind: "failed" }
  | null;

type UseAdmAreaMappingParams = {
  datasetId?: string;
  /** Called once rows have actually been written. `fromPartial` is true when it
   * took a Save Anyway, which closes the whole dialog rather than going back. */
  onMapped: (attributeId: string, fromPartial: boolean) => void;
  /** The dataset already has a mapping, so a save replaces it. */
  isEditing?: boolean;
};

const useAdmAreaMapping = ({
  datasetId,
  onMapped,
  isEditing = false
}: UseAdmAreaMappingParams) => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const [selectedAttribute, setSelectedAttribute] = useState<string>();
  const [outcome, setOutcome] = useState<AdmAreaMappingOutcome>(null);
  // Which attempt is in flight: the first one, which validates the codes, or the
  // one that accepts a partial match and writes it.
  const [pendingRun, setPendingRun] = useState<"map" | "confirm">();

  const { mutate: mutateGenerateAdmArea, isPending: isMapping } =
    useGenerateDatasetAdmArea(datasetId as string);

  const handleResponse = (
    response: GenerateAdmAreaResponse,
    attributeId: string,
    fromPartial: boolean
  ) => {
    // The confirming call reports the same counts back, so a partial status on
    // it means the rows were written, not that we have to ask again.
    if (response.status === "PARTIAL_SUCCESS" && !fromPartial) {
      setOutcome({ kind: "partial", counts: response.data });
      return;
    }
    if (response.status === "FAILED") {
      setOutcome({ kind: "failed" });
      return;
    }
    setOutcome(null);
    // The mapping adds an Adm. Area ID column and can set the dataset's country.
    // These refresh the screens that read the dataset through react-query; the
    // detail page reads it over its own stream, which `onMapped` reopens.
    queryClient.invalidateQueries({
      queryKey: ["dataset-metadata", datasetId]
    });
    queryClient.invalidateQueries({ queryKey: ["dataset-data", datasetId] });
    showToast({
      type: EToastType.SUCCESS,
      message: (
        <NotificationContent
          title={
            isEditing
              ? MESSAGE.ADM_AREA_MAPPING_UPDATE_SUCCESS
              : MESSAGE.ADM_AREA_MAPPING_SUCCESS
          }
          description={
            isEditing
              ? MESSAGE.ADM_AREA_MAPPING_UPDATE_SUCCESS_DESCRIPTION
              : MESSAGE.ADM_AREA_MAPPING_SUCCESS_DESCRIPTION
          }
        />
      )
    });
    onMapped(attributeId, fromPartial);
  };

  const runMapping = (confirmPartial: boolean) => {
    if (!datasetId || !selectedAttribute) return;
    const attributeId = selectedAttribute;
    // A fresh attempt clears what the last one said, so a retry does not run
    // under the alert it is trying to clear. Accepting a partial match keeps its
    // warning on screen, since that view keeps its buttons.
    if (!confirmPartial) setOutcome(null);
    setPendingRun(confirmPartial ? "confirm" : "map");
    mutateGenerateAdmArea(
      {
        source_attribute_id: attributeId,
        ...(confirmPartial ? { confirm_partial: true } : {})
      },
      {
        onSuccess: (response) => {
          setPendingRun(undefined);
          handleResponse(response, attributeId, confirmPartial);
        },
        onError: () => {
          setPendingRun(undefined);
          setOutcome({ kind: "failed" });
        }
      }
    );
  };

  const selectAttribute = (attributeId: string) => {
    setSelectedAttribute(attributeId);
    // A different column is a different attempt; drop what the last one said.
    setOutcome(null);
  };

  /** Leaving without saving goes back to the mapping the dataset already has. */
  const resetMapping = (mappedAttribute?: string) => {
    setSelectedAttribute(mappedAttribute);
    setOutcome(null);
  };

  return {
    selectedAttribute,
    outcome,
    /** First attempt: the whole footer becomes one Validating button. */
    isValidating: isMapping && pendingRun === "map",
    /** Accepting a partial match: the buttons stay, Save Anyway reads Saving. */
    isConfirmingPartial: isMapping && pendingRun === "confirm",
    selectAttribute,
    resetMapping,
    mapAttribute: () => runMapping(false),
    confirmPartialMapping: () => runMapping(true)
  };
};

export default useAdmAreaMapping;
